export const calculateAdjustment = (currentStr, adjustmentStr, type) => {
  if (!currentStr || !adjustmentStr) return null;
  const current = parseFloat(currentStr);
  const adjustment = parseFloat(adjustmentStr);

  if (isNaN(current) || isNaN(adjustment)) return null;

  let newValue;
  switch (type) {
    case 'absolute':
      newValue = adjustment;
      break;
    case 'percentage':
      newValue = current !== 0 ? current * (1 + adjustment / 100) : adjustment;
      break;
    case 'multiplier':
      newValue = current !== 0 ? current * adjustment : adjustment;
      break;
    default:
      newValue = current;
  }

  newValue = Math.max(0, newValue);

  const rawChange = newValue - current;
  const change = rawChange;
  const changePercentage = current !== 0 ? ((rawChange / current) * 100) : null;

  return {
    newValue: Math.round(newValue * 100) / 100,
    change: Math.round(change * 100) / 100,
    changePercentage: changePercentage !== null ? Math.round(changePercentage * 100) / 100 : null
  };
};

const toFiniteNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const medianOfNumbers = (values) => {
  const nums = (Array.isArray(values) ? values : [])
    .map((value) => toFiniteNumber(value))
    .filter((value) => value != null)
    .sort((a, b) => a - b);
  if (nums.length === 0) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 === 0 ? (nums[mid - 1] + nums[mid]) / 2 : nums[mid];
};

export const buildAnomalyFallback = (seriesValues, baseValues = [], promoValues = [], historicalSummary = {}) => {
  const values = (Array.isArray(seriesValues) ? seriesValues : []).map((value) => toFiniteNumber(value) ?? 0);

  const flags = Array(values.length).fill(false);
  const notes = Array(values.length).fill('');
  const strengths = Array(values.length).fill(0);
  const directions = Array(values.length).fill('');
  const reasons = Array(values.length).fill('');

  if (values.length < 3) return { flags, notes, strengths, directions, reasons };

  const window = 7;
  const overallMean = values.reduce((a, b) => a + b, 0) / values.length;
  const historicalMedian = toFiniteNumber(historicalSummary?.historical_median ?? historicalSummary?.median);
  const baselineReference = historicalMedian && historicalMedian > 0
    ? historicalMedian
    : (medianOfNumbers(values) ?? overallMean);
  const candidateMap = new Map();

  const registerCandidate = (index, score, direction, note, reason) => {
    const existing = candidateMap.get(index);
    if (!existing || score > existing.score) {
      candidateMap.set(index, { index, score, direction, note, reason });
    }
  };

  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - window);
    const end = Math.min(values.length, i + window + 1);
    const neighbors = values.slice(start, end);
    const localMean = neighbors.reduce((a, b) => a + b, 0) / neighbors.length;
    const localVariance = neighbors.reduce((s, v) => s + (v - localMean) ** 2, 0) / neighbors.length;
    const localStd = Math.sqrt(localVariance);
    const value = values[i];
    const deviationPct = baselineReference > 0 ? Math.abs(((value - baselineReference) / baselineReference) * 100) : 0;
    const isAboveBaseline = value >= baselineReference;

    const hasLocalOutlier = localStd > 0 && Math.abs(value - localMean) / localStd > 3.0;
    const hasMedianOutlier = deviationPct >= 30;
    if (!hasLocalOutlier && !hasMedianOutlier) continue;

    const zScore = localStd > 0 ? Math.abs(value - localMean) / localStd : 0;
    const direction = isAboveBaseline ? 'spike_up' : 'spike_down';
    const note = hasLocalOutlier
      ? (
        direction === 'spike_up'
          ? `Spike detected: ${zScore.toFixed(1)} sigma above local 7-day pattern.`
          : `Drop detected: ${zScore.toFixed(1)} sigma below local 7-day pattern.`
      )
      : (
        direction === 'spike_up'
          ? `Spike detected vs historical median (${deviationPct.toFixed(1)}% above baseline).`
          : `Drop detected vs historical median (${deviationPct.toFixed(1)}% below baseline).`
      );
    const reason = hasLocalOutlier
      ? `Value deviates (${zScore.toFixed(1)} sigma) from rolling 7-day neighborhood pattern.`
      : `Value is ${deviationPct.toFixed(1)}% ${isAboveBaseline ? 'above' : 'below'} the historical median.`;
    const score = Math.max(zScore * 10, deviationPct);
    registerCandidate(i, score, direction, note, reason);
  }

  if (candidateMap.size === 0) {
    const rankedExtremes = values
      .map((value, index) => {
        const deviationPct = baselineReference > 0 ? Math.abs(((value - baselineReference) / baselineReference) * 100) : 0;
        return { index, value, deviationPct };
      })
      .filter((item) => item.deviationPct >= 25)
      .sort((a, b) => b.deviationPct - a.deviationPct)
      .slice(0, 2);

    for (const item of rankedExtremes) {
      const direction = item.value >= baselineReference ? 'spike_up' : 'spike_down';
      const note = direction === 'spike_up'
        ? `Spike detected vs historical median (${item.deviationPct.toFixed(1)}% above baseline).`
        : `Drop detected vs historical median (${item.deviationPct.toFixed(1)}% below baseline).`;
      const reason = `Value is ${item.deviationPct.toFixed(1)}% ${item.value >= baselineReference ? 'above' : 'below'} the historical median.`;
      registerCandidate(item.index, item.deviationPct, direction, note, reason);
    }
  }

  const orderedCandidates = [...candidateMap.values()].sort((a, b) => b.score - a.score);
  const maxAnomalies = Math.min(12, Math.max(3, Math.floor(values.length / 5) || 3));
  const selectedCandidates = [];
  const canAdd = (candidate) => selectedCandidates.every((item) => Math.abs(candidate.index - item.index) >= 3);
  const addBestMatching = (predicate = null) => {
    for (const candidate of orderedCandidates) {
      if (predicate && !predicate(candidate)) continue;
      if (!canAdd(candidate)) continue;
      if (selectedCandidates.some((item) => item.index === candidate.index)) continue;
      selectedCandidates.push(candidate);
      return true;
    }
    return false;
  };

  if (orderedCandidates.length) {
    addBestMatching();
    if (selectedCandidates.length < maxAnomalies && selectedCandidates[0]?.direction) {
      addBestMatching((candidate) => candidate.direction !== selectedCandidates[0].direction);
    }
    while (selectedCandidates.length < maxAnomalies && addBestMatching()) {
      // Keep filling remaining slots with the strongest non-overlapping outliers.
    }
  }

  if (!selectedCandidates.length && orderedCandidates.length) {
    selectedCandidates.push(orderedCandidates[0]);
  }

  for (const candidate of selectedCandidates) {
    if (!candidate) continue;
    flags[candidate.index] = true;
    notes[candidate.index] = candidate.note;
    strengths[candidate.index] = Number(Math.min(candidate.score, 100).toFixed(2));
    directions[candidate.index] = candidate.direction;
    reasons[candidate.index] = candidate.reason;
  }

  return { flags, notes, strengths, directions, reasons };
};
